import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../../shared/models/content.models';
import { CountriesService } from '../../shared/services/countries.service';

/** Mirrors API `cta` on home marketing payload when customized there. */
export type ConsultationCtaModel = {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  bullets: string[];
  formHeading?: string;
  legalLine?: string;
};

export const DEFAULT_MARKETING_CONTACT_CTA: ConsultationCtaModel = {
  eyebrow: 'Ready to begin?',
  title: 'Book a Free Consultation',
  subtitle:
    'Talk to a senior counselor in 24 hours. No spam, no sales pitch, no upsells — just a clear plan tailored to your goals.',
  bullets: [
    'Free 45-minute consultation',
    'University shortlist within a week',
    'Scholarship discovery built in',
  ],
  formHeading: 'Book a Free Consultation',
  legalLine: 'By submitting you agree to our terms & privacy policy.',
};

@Component({
  selector: 'app-marketing-consultation-cta',
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="h1-cta" id="cta">
      <div class="h1-cta__inner">
        <div class="h1-cta__copy">
          <span class="h1-eyebrow h1-eyebrow--on-dark">{{ displayEyebrow }}</span>
          <h2>
            Take the <span class="h1-cta__highlight">Next Step</span><br />
            of your global career.
          </h2>
          @if (effectiveCta.subtitle) {
            <p>{{ effectiveCta.subtitle }}</p>
          }
          @if (effectiveCta.bullets.length) {
            <ul class="h1-cta__bullets">
              @for (b of effectiveCta.bullets; track b) {
                <li><span aria-hidden="true">✓</span> {{ b }}</li>
              }
            </ul>
          }
        </div>

        <form class="h1-cta__form" (ngSubmit)="submit()" novalidate>
          <h3>{{ formHeadingText }}</h3>
          <label>
            <span>Full name</span>
            <input
              type="text"
              name="fullName"
              [(ngModel)]="form.fullName"
              required
              placeholder="e.g. Aanya Patel"
              autocomplete="name"
            />
          </label>
          <label>
            <span>Email</span>
            <input
              type="email"
              name="email"
              [(ngModel)]="form.email"
              required
              placeholder="you@email.com"
              autocomplete="email"
            />
          </label>
          <label>
            <span>Phone</span>
            <input
              type="tel"
              name="phone"
              [(ngModel)]="form.phone"
              required
              placeholder="+91 98765 43210"
              autocomplete="tel"
            />
          </label>
          <label>
            <span>Preferred destination</span>
            <select name="destination" [(ngModel)]="form.destination">
              <option value="" disabled>Select a country</option>
              @for (d of destinationList(); track d) {
                <option [value]="d">{{ d }}</option>
              }
            </select>
          </label>
          @if (submitError()) {
            <p class="text-sm font-medium text-red-600">{{ submitError() }}</p>
          }
          @if (submitOk()) {
            <p class="text-sm font-medium text-emerald-700">{{ submitOkMessage() }}</p>
          }
          <button
            type="submit"
            class="h1-btn h1-btn--accent h1-btn--lg h1-btn--block"
            [disabled]="submitting()"
          >
            {{ submitting() ? 'Sending…' : 'Request callback' }}
          </button>
          <p class="h1-cta__small">{{ legalLineText }}</p>
        </form>
      </div>
    </section>
  `,
})
export class MarketingConsultationCtaComponent implements OnInit, OnChanges {
  private readonly api = inject(ApiService);
  private readonly countries = inject(CountriesService);

  /** Optional override (e.g. home marketing CMS). Countries use built-in defaults. */
  @Input() cta?: ConsultationCtaModel;
  @Input() destinationOptions: string[] = [];
  /** When set (country pages), pre-selects preferred destination in the form. */
  @Input() preferredDestination?: string;
  @Input() sourcePage = '/';

  readonly form = { fullName: '', email: '', phone: '', destination: '' };
  readonly submitting = signal(false);
  readonly submitOk = signal(false);
  readonly submitOkMessage = signal('');
  readonly submitError = signal<string | null>(null);
  readonly destinationList = signal<string[]>([]);

  ngOnInit(): void {
    this.syncDestinationList();
    this.applyPreferredDestination();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preferredDestination'] || changes['destinationOptions']) {
      this.applyPreferredDestination();
      this.syncDestinationList();
    }
  }

  get effectiveCta(): ConsultationCtaModel {
    return this.cta ?? DEFAULT_MARKETING_CONTACT_CTA;
  }

  get displayEyebrow(): string {
    return (this.effectiveCta.eyebrow && this.effectiveCta.eyebrow.trim()) || 'Ready to begin?';
  }

  get formHeadingText(): string {
    return (
      (this.effectiveCta.formHeading && this.effectiveCta.formHeading.trim()) ||
      (this.effectiveCta.title && this.effectiveCta.title.trim()) ||
      'Book a Free Consultation'
    );
  }

  get legalLineText(): string {
    return (
      (this.effectiveCta.legalLine && this.effectiveCta.legalLine.trim()) ||
      'By submitting you agree to our terms & privacy policy.'
    );
  }

  submit(): void {
    if (!this.form.fullName?.trim() || !this.form.email?.trim() || !this.form.phone?.trim()) {
      this.submitError.set('Please fill in name, email, and phone.');
      return;
    }
    this.submitting.set(true);
    this.submitError.set(null);
    this.submitOk.set(false);
    this.submitOkMessage.set('');
    this.api
      .post<ApiEnvelope<unknown>>('public/leads', {
        fullName: this.form.fullName.trim(),
        email: this.form.email.trim(),
        phoneNumber: this.form.phone.trim(),
        preferredDestination: this.form.destination || undefined,
        leadSource: 'Website',
        sourcePage: this.sourcePage,
      })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.submitOk.set(true);
          this.submitOkMessage.set('Thank you. We have received your details and will be in touch shortly.');
          this.resetFormFields();
        },
        error: (err) => {
          this.submitting.set(false);
          const status = err?.status;
          const message = err?.error?.message as string | undefined;
          if (status === 409 && message) {
            this.submitOk.set(true);
            this.submitOkMessage.set(message);
            this.resetFormFields();
            return;
          }
          this.submitError.set(message ?? 'Something went wrong.');
        },
      });
  }

  private resetFormFields(): void {
    this.form.fullName = '';
    this.form.email = '';
    this.form.phone = '';
    this.form.destination = '';
    this.applyPreferredDestination();
  }

  private applyPreferredDestination(): void {
    const pref = this.preferredDestination?.trim();
    if (pref) {
      this.form.destination = pref;
    }
  }

  private syncDestinationList(): void {
    const fromInput = this.destinationOptions.map((d) => d.trim()).filter(Boolean);
    this.countries.listPublicCountries().subscribe({
      next: (list) => {
        const fromApi = list.map((c) => c.name?.trim()).filter((v): v is string => Boolean(v));
        const names = Array.from(new Set([...fromApi, ...fromInput])).sort((a, b) => a.localeCompare(b));
        this.destinationList.set(this.ensurePreferredInList(names));
        this.applyPreferredDestination();
      },
      error: () => {
        this.destinationList.set(this.ensurePreferredInList(fromInput));
      },
    });
  }

  private ensurePreferredInList(names: string[]): string[] {
    const pref = this.preferredDestination?.trim();
    if (!pref || names.includes(pref)) return names;
    return [pref, ...names];
  }
}
